import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trophy, ChevronRight, Star, Flame, BookOpenCheck, MessageSquare, User, BookOpen, Users, Image } from 'lucide-react';
import { checkAuth } from '../auth/config';
import NavigationCarousel from '../components/NavigationCarousel';
import { useTheme } from '../contexts/ThemeContext';
import DashboardHeader from '../components/DashboardHeader';
import RAGChat from '../components/RAGChat';

const Home = () => {
  // Check for signup user first, then demo user as fallback
  const signupUser = JSON.parse(localStorage.getItem('user') || 'null');
  const demoUser = checkAuth();
  const user = signupUser || demoUser;
  
  // Check if current user is a demo user
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isCopilotExpanded, setIsCopilotExpanded] = useState(false);
  const [hoveredConcept, setHoveredConcept] = useState(null);
  const { isDarkMode } = useTheme();
  const copilotRef = useRef(null);

  // Scroll to chatbot when expanded
  useEffect(() => {
    if (isCopilotExpanded && copilotRef.current) {
      copilotRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isCopilotExpanded]);

  // Handle navigation carousel clicks
  const handleNavigationClick = (item) => {
    if (item.path === '/login') {
      localStorage.removeItem('user');
      localStorage.removeItem('onboardingComplete');
      navigate('/login');
    } else {
      navigate(item.path);
    }
  };

  // Handle sidebar toggle
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };
  
  // Get user role from localStorage
  // const userRole = localStorage.getItem('userRole') || 'learner'; // Unused variable

  const stats = [
    { label: 'Current Streak', value: '7', icon: Flame },
    { label: 'XP Earned', value: '2,340', icon: Star },
    { label: 'Courses', value: '12', icon: BookOpenCheck },
    { label: 'Rank', value: '#156', icon: Trophy }
  ];

  const conceptData = [
    {
      id: 'ai-generated-design',
      title: 'AI Generated Design',
      author: 'Dr. Sarah Chen',
      description: 'Explore cutting-edge AI tools for architectural design and automated building modeling. Learn how artificial intelligence is revolutionizing the creative process in architecture.',
      rating: 4.8,
      reviews: 127,
      image: '/assets/AI GENERATED DESIGN .png'
    },
    {
      id: 'international-projects',
      title: 'International Projects',
      author: 'Prof. Ahmed Al-Mansouri',
      description: 'Study landmark international architectural projects and understand global design principles. Discover how cultural context influences modern architecture worldwide.',
      rating: 4.6,
      reviews: 89,
      image: '/assets/INTERNATIONAL PROJECTS.png'
    },
    {
      id: 'new-age-building',
      title: 'New Age Building',
      author: 'Architect Maria Rodriguez',
      description: 'Dive into futuristic building technologies and sustainable design innovations. Learn about smart buildings, green architecture, and next-generation construction methods.',
      rating: 4.9,
      reviews: 203,
      image: '/assets/Newbuilding.jpeg'
    }
  ];

  // const quickActions = [ // Unused variable
  //   { 
  //     title: 'Study Resources', 
  //     subtitle: 'Continue learning', 
  //     icon: BookOpen, 
  //     path: '/app/study',
  //     color: 'bg-blue-50 text-blue-700 border-blue-100'
  //   },
  //   { 
  //     title: 'Challenges', 
  //     subtitle: 'Test your skills', 
  //     icon: Trophy, 
  //     path: '/app/challenges',
  //     color: 'bg-amber-50 text-amber-700 border-amber-100'
  //   },
  //   { 
  //     title: 'Community', 
  //     subtitle: 'Connect with peers', 
  //     icon: Users, 
  //     path: '/app/community',
  //     color: 'bg-green-50 text-green-700 border-green-100'
  //   },
  //   { 
  //     title: 'Progress', 
  //     subtitle: 'View your stats', 
  //     icon: BarChart3, 
  //     path: '/app/profile',
  //     color: 'bg-purple-50 text-purple-700 border-purple-100'
  //   }
  // ];

  // const featuredContent = [ // Unused variable
  //   {
  //     title: 'VR Architecture Studio',
  //     subtitle: 'Immersive design experience',
  //     duration: '45 min',
  //     difficulty: 'Intermediate',
  //     image: 'vr-studio'
  //   },
  //   {
  //     title: 'Sustainable Design',
  //     subtitle: 'Environmental architecture',
  //     duration: '30 min', 
  //     difficulty: 'Beginner',
  //     image: 'sustainable'
  //   },
  //   {
  //     title: 'Urban Planning',
  //     subtitle: 'City development basics',
  //     duration: '60 min',
  //     difficulty: 'Advanced',
  //     image: 'urban'
  //   }
  // ];

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Fixed Navigation Overlay - Extends to Top */}
      <div className="fixed top-0 left-0 z-50" style={{ zIndex: 60 }}>
        <NavigationCarousel
          menuItems={[
            { label: 'HOME', path: '/app' },
            { label: 'STUDY', path: '/app/study' },
            { label: 'CHALLENGES', path: '/app/challenges' },
            { label: 'COMMUNITY', path: '/app/community' },
            { label: 'PIN UP', path: '/app/pinup' },
            { label: 'PROGRESS', path: '/app/progress' },
            { label: 'PROFILE', path: '/app/profile' },
            { label: 'LOG OUT', path: '/login', isLogout: true }
          ]}
          onItemClick={handleNavigationClick}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      </div>

      {/* Student Dashboard Header */}
      <DashboardHeader title="STUDENT DASHBOARD" isSidebarCollapsed={isSidebarCollapsed} />

      <div className="max-w-7xl mx-auto px-6 py-8" style={{ 
        marginLeft: isSidebarCollapsed ? '90px' : '14vw',
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Welcome Section */}
        <section className="mb-12">
          <div className="mb-8">
            <h2 className={`text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3`}>
              Welcome back, {user?.name || 'Ahmed Al Mansouri'}
            </h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-xl font-medium`}>Your learning dashboard</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-1 mb-6 max-w-md">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <Link key={idx} to="/app/progress" className={`${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'} rounded p-1.5 text-center transition-colors cursor-pointer`}>
                  <Icon size={13} className="mx-auto mb-0.5 text-[#AC5757]" />
                  <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} leading-tight`}>{stat.value}</div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} leading-tight`}>{stat.label}</div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* AXORA Copilot */}
        <section className="mb-8" ref={copilotRef}>
          <div className={`relative ${isDarkMode ? 'bg-gray-900' : 'bg-white'} rounded-2xl shadow-sm overflow-hidden transition-all duration-500 ${isCopilotExpanded ? 'ring-1 ring-[#AC5757]/40 shadow-xl' : ''}`}>
            {isCopilotExpanded && (
              <button
                onClick={() => setIsCopilotExpanded(false)}
                className={`absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-full transition text-[#AC5757] ${
                  isDarkMode
                    ? 'hover:text-white hover:bg-gray-800'
                    : 'hover:text-white hover:bg-[#AC5757]'
                }`}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <div className="px-6 pt-6">
              <h3 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-black'} mb-2`}>
                {['Hello there,', 'How can I help you?'].map((s, i) => (
                  <span key={i} className="block">{s}</span>
                ))}
              </h3>
            </div>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden`}
              style={{
                height: isCopilotExpanded ? '70vh' : '420px',
                maxHeight: isCopilotExpanded ? '78vh' : '420px'
              }}
            >
              <RAGChat
                darkMode={isDarkMode}
                hideHeader
                className="h-full"
                initialMessage={'Hello there,\nHow can I help you?'}
                expanded={isCopilotExpanded}
                onExpandChange={setIsCopilotExpanded}
              />
            </div>
          </div>
        </section>

        {/* Recent Activity */}
        <section className="mb-6">
          <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Recent Activity</h3>
          <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700 divide-gray-700' : 'bg-white border-gray-200 divide-gray-200'} rounded-xl border divide-y`}>
            <Link to="/app/study?course=spatial-reasoning-fundamentals&tab=courses" className={`p-4 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors cursor-pointer block`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div>
                    <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} text-sm`}>Spatial Reasoning Fundamentals</h4>
                    <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-xs mt-1`}>Progress: 65% complete</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last studied</div>
                  <ChevronRight size={16} className={`${isDarkMode ? 'text-gray-400' : 'text-gray-400'} mt-1`} />
                </div>
              </div>
            </Link>
            <Link to="/app/study?course=architecture-history-quiz&tab=tests" className={`p-4 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors cursor-pointer block`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <div>
                    <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} text-sm`}>Architecture History Quiz</h4>
                    <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-xs mt-1`}>Score: 76/100</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Completed</div>
                  <ChevronRight size={16} className={`${isDarkMode ? 'text-gray-400' : 'text-gray-400'} mt-1`} />
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Learning Tools */}
        <section className={`mb-2 sm:mb-3 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-2xl p-3`}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
            <Link to="/app/study" className="group rounded-2xl p-2 sm:p-3 lg:p-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col justify-end" style={{ backgroundColor: '#AC5757' }}>
              <div className="aspect-square rounded-xl overflow-hidden scale-117 flex items-center justify-center mb-1">
                <BookOpen className="w-20 h-20 text-white" />
              </div>
              <h4 className="font-bold text-white text-lg sm:text-2xl lg:text-3xl text-center" style={{ fontFamily: 'serif' }}>STUDY</h4>
            </Link>
            
            <Link to="/app/challenges" className="group rounded-2xl p-2 sm:p-3 lg:p-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col justify-end" style={{ backgroundColor: '#AC5757' }}>
              <div className="aspect-square rounded-xl overflow-hidden scale-117 flex items-center justify-center mb-1">
                <Trophy className="w-20 h-20 text-white" />
              </div>
              <h4 className="font-bold text-white text-lg sm:text-2xl lg:text-3xl text-center" style={{ fontFamily: 'serif' }}>CHALLENGES</h4>
            </Link>
            
            <Link to="/app/community" className="group rounded-2xl p-2 sm:p-3 lg:p-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col justify-end" style={{ backgroundColor: '#AC5757' }}>
              <div className="aspect-square rounded-xl overflow-hidden scale-117 flex items-center justify-center mb-1">
                <Users className="w-20 h-20 text-white" />
              </div>
              <h4 className="font-bold text-white text-lg sm:text-2xl lg:text-3xl text-center" style={{ fontFamily: 'serif' }}>COMMUNITY</h4>
            </Link>
            
            <Link to="/app/pinup" className="group rounded-2xl p-2 sm:p-3 lg:p-4 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col justify-end" style={{ backgroundColor: '#AC5757' }}>
              <div className="aspect-square rounded-xl overflow-hidden scale-117 flex items-center justify-center mb-1">
                <Image className="w-20 h-20 text-white" />
              </div>
              <h4 className="font-bold text-white text-lg sm:text-2xl lg:text-3xl text-center" style={{ fontFamily: 'serif' }}>PIN UP</h4>
            </Link>
          </div>
        </section>

        {/* Explore Concepts */}
        <section className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>EXPLORE CONCEPTS</h3>
            <Link 
              to="/app/concepts" 
              className="text-[#AC5757] font-semibold hover:text-[#8A4A4A] transition-colors text-sm sm:text-base"
            >
              View all →
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 relative">
            {conceptData.map((concept) => (
              <div 
                key={concept.id}
                className="relative group cursor-pointer rounded-2xl overflow-hidden"
                style={{ height: '300px' }}
                onMouseEnter={() => setHoveredConcept(concept.id)}
                onMouseLeave={() => setHoveredConcept(null)}
              >
                {/* Background image - always visible */}
                <img src={concept.image} alt={concept.title} className="w-full h-full object-cover" />
                
                {/* Top half overlay - reduced transparency on hover */}
                <div className={`absolute inset-0 h-1/2 transition-opacity duration-300 ${
                  hoveredConcept === concept.id ? 'bg-black/50' : 'bg-black/0'
                }`}></div>
                
                {/* Normal state - bottom overlay with title and author */}
                {hoveredConcept !== concept.id && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <h4 className="font-bold text-white text-lg mb-1">{concept.title}</h4>
                    <p className="text-white/90 text-sm">{concept.author}</p>
                  </div>
                )}
                
                {/* Hover state - bottom half with split layout */}
                {hoveredConcept === concept.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-white">
                    <div className="h-full flex">
                      {/* Left side - concept image */}
                      <div className="w-1/2 p-3 flex items-center justify-center">
                        <img 
                          src={concept.image} 
                          alt={concept.title}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      
                      {/* Right side - description */}
                      <div className="w-1/2 p-3 flex flex-col justify-center">
                        <h4 className="font-bold text-gray-900 text-sm mb-1">{concept.title}</h4>
                        <p className="text-xs font-bold text-gray-900 mb-1">{concept.author}</p>
                        <p className="text-xs text-gray-700 mb-2 leading-relaxed" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>{concept.description}</p>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1">
                            <Star size={10} className="text-yellow-400 fill-current" />
                            <span className="text-xs font-semibold text-gray-900">{concept.rating}</span>
                          </div>
                          <span className="text-xs text-gray-500">({concept.reviews})</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Clickable overlay for navigation */}
                <Link to={`/app/concepts?concept=${concept.id}`} className="absolute inset-0 z-40"></Link>
              </div>
            ))}
          </div>
        </section>


      </div>
    </div>
  );
};

export default Home;